import { useState } from 'react';
import { clsx } from 'clsx';
import { ImageOff } from 'lucide-react';
import {
  apiMediaSrc,
  type StrategyDocketUsage,
  type StrategyUsageAngle,
  type StrategyUsageHandoff,
} from '@/services/api';
import { Chip, Collapsible, Empty, ErrorNote, Loading, Pre, Section, WarningBanner, formatDate, toText } from './shared';

function ItemLink({ id, label, onOpenItem }: { id: string; label?: string | null; onOpenItem?: (id: string) => void }) {
  if (!onOpenItem) return <span className="font-mono text-xs">{label || id}</span>;
  return (
    <button type="button" onClick={() => onOpenItem(id)} className="text-left text-navy-900 hover:text-brand-teal-dark underline decoration-slate-300 underline-offset-2">
      {label ? <span>{label}</span> : null}
      <span className="block font-mono text-[10px] text-slate-500">{id}</span>
    </button>
  );
}

function Thumb({ url }: { url: string }) {
  const src = apiMediaSrc(url);
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="w-14 h-14 rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400" title={url}>
        <ImageOff size={14} />
      </a>
    );
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open asset">
      <img src={src} alt="" loading="lazy" className="w-14 h-14 rounded border border-slate-200 object-cover bg-slate-100" onError={() => setFailed(true)} />
    </a>
  );
}

function selectionSummary(sel: StrategyUsageAngle['template_selection']): string {
  if (!sel) return '';
  if (typeof sel === 'string') return sel;
  const parts: string[] = [];
  if (sel.source) parts.push(String(sel.source));
  else if (sel.selector_version) parts.push(String(sel.selector_version));
  if (typeof sel.score === 'number') parts.push(`score ${sel.score.toFixed(2)}`);
  if (typeof sel.rank === 'number') parts.push(`rank ${sel.rank}`);
  return parts.join(' · ');
}

function StatusChip({ status, dryRun }: { status?: string | null; dryRun?: boolean | null }) {
  const s = (status || '').toLowerCase();
  const tone = s.includes('fail') || s.includes('error') ? 'red' : s.includes('complete') || s.includes('success') || s === 'done' ? 'emerald' : 'slate';
  return (
    <span className="inline-flex gap-1 flex-wrap">
      {status && <Chip tone={tone}>{status}</Chip>}
      {dryRun && <Chip tone="amber">dry run</Chip>}
    </span>
  );
}

export function AnglesTable({
  angles,
  onOpenItem,
  showClient = true,
}: {
  angles: StrategyUsageAngle[];
  onOpenItem?: (id: string) => void;
  showClient?: boolean;
}) {
  if (angles.length === 0) return <Empty>No angles cite this.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
            {showClient && <th className="py-2 pr-3 font-medium">Client</th>}
            <th className="py-2 pr-3 font-medium">Brief</th>
            <th className="py-2 pr-3 font-medium">Angle hook</th>
            <th className="py-2 pr-3 font-medium">Template</th>
            <th className="py-2 pr-3 font-medium">Cites</th>
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {angles.map((a) => (
            <tr key={`${a.client_uid}:${a.brief_id}:${a.angle_id}`} className="align-top">
              {showClient && <td className="py-2 pr-3 font-mono text-xs text-slate-600">{a.client_uid}</td>}
              <td className="py-2 pr-3">
                <div className="text-slate-800">{a.brief_name || '—'}</div>
                <div className="font-mono text-[10px] text-slate-400">{a.brief_id}</div>
              </td>
              <td className="py-2 pr-3 max-w-xs text-slate-700">{a.hook || <span className="text-slate-400">—</span>}</td>
              <td className="py-2 pr-3">
                {a.template_id ? <ItemLink id={a.template_id} onOpenItem={onOpenItem} /> : <span className="text-slate-400">—</span>}
                {selectionSummary(a.template_selection) && (
                  <div className="text-[10px] text-slate-500 mt-0.5">{selectionSummary(a.template_selection)}</div>
                )}
              </td>
              <td className="py-2 pr-3">
                <div className="flex flex-wrap gap-1 max-w-[220px]">
                  {a.evidence_ids.map((id) =>
                    onOpenItem ? (
                      <button key={id} type="button" onClick={() => onOpenItem(id)} className="font-mono text-[10px] px-1 py-0.5 rounded border border-slate-200 hover:border-brand-teal">
                        {id}
                      </button>
                    ) : (
                      <span key={id} className="font-mono text-[10px] px-1 py-0.5 rounded border border-slate-200">{id}</span>
                    ),
                  )}
                </div>
              </td>
              <td className="py-2 pr-3"><StatusChip status={a.status} /></td>
              <td className="py-2 text-xs text-slate-500 whitespace-nowrap">{formatDate(a.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HandoffsTable({
  handoffs,
  onOpenItem,
  showClient = true,
}: {
  handoffs: StrategyUsageHandoff[];
  onOpenItem?: (id: string) => void;
  showClient?: boolean;
}) {
  if (handoffs.length === 0) return <Empty>No handoffs executed this.</Empty>;
  return (
    <ul className="divide-y divide-slate-100">
      {handoffs.map((h, i) => {
        const t = h.template;
        return (
          <li key={h.handoff_id || `${h.brief_id}:${i}`} className="py-3 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-0.5">
                <div className="text-sm text-slate-800">
                  {showClient && <span className="font-mono text-xs text-slate-500 mr-2">{h.client_uid}</span>}
                  {h.brief_name || h.brief_id}
                  {h.angle_id && <span className="font-mono text-[10px] text-slate-400 ml-2">angle {h.angle_id}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  {h.provider_id && <Chip tone="navy">{h.provider_id}</Chip>}
                  <StatusChip status={h.status} dryRun={h.dry_run} />
                  {t?.selection_source && <Chip>selected: {t.selection_source}</Chip>}
                  {t?.prompt_source && <Chip>prompt: {t.prompt_source}</Chip>}
                  {t && t.in_prompt != null && (
                    <Chip tone={t.in_prompt ? 'emerald' : 'amber'}>{t.in_prompt ? 'template in prompt' : 'template NOT in prompt'}</Chip>
                  )}
                  {t?.pack_version && <Chip mono>{t.pack_version}</Chip>}
                </div>
              </div>
              <div className="text-xs text-slate-500 whitespace-nowrap">{formatDate(h.created_at)}</div>
            </div>
            {(h.template_id || t?.name) && (
              <div className="text-xs text-slate-600 flex items-start gap-2">
                <span className="text-slate-400 pt-0.5">Template</span>
                {h.template_id ? <ItemLink id={h.template_id} label={t?.name} onOpenItem={onOpenItem} /> : <span>{t?.name}</span>}
                {t?.scope && <span className="text-[10px] text-slate-400 pt-0.5">{t.scope}</span>}
              </div>
            )}
            {t?.unbound_slots && t.unbound_slots.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 text-xs">
                <span className="text-amber-700">Unbound slots:</span>
                {t.unbound_slots.map((s) => <Chip key={s} tone="amber" mono>{`{${s}}`}</Chip>)}
              </div>
            )}
            {h.asset_urls.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {h.asset_urls.map((u) => <Thumb key={u} url={u} />)}
              </div>
            )}
            {h.core_asset_ids.length > 0 && (
              <div className="text-[10px] text-slate-400 font-mono">core assets: {h.core_asset_ids.join(', ')}</div>
            )}
            {h.prompt_used && (
              <Collapsible label={`Prompt used (${h.prompt_used.length} chars)`}>
                <Pre>{h.prompt_used}</Pre>
              </Collapsible>
            )}
            {t?.bound_recreation_prompt && (
              <Collapsible label="Bound recreation prompt">
                <Pre>{t.bound_recreation_prompt}</Pre>
              </Collapsible>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function UnavailableMembers({ unavailable }: { unavailable: StrategyDocketUsage['unavailable'] }) {
  if (!unavailable.length) return null;
  return (
    <WarningBanner>
      Usage could not be read for {unavailable.length} member{unavailable.length === 1 ? '' : 's'}:
      <ul className="mt-1 text-xs">
        {unavailable.map((u) => (
          <li key={u.client_uid}>
            <span className="font-mono">{u.client_uid}</span>
            {u.reason ? ` — ${toText(u.reason)}` : ''}
          </li>
        ))}
      </ul>
    </WarningBanner>
  );
}

/** The docket-wide Usage tab. */
export function DocketUsageTab({
  usage,
  loading,
  error,
  onOpenItem,
  onReload,
}: {
  usage: StrategyDocketUsage | null;
  loading: boolean;
  error: string | null;
  onOpenItem: (id: string) => void;
  onReload: () => void;
}) {
  const [client, setClient] = useState('all');
  if (loading && !usage) return <Loading label="Scanning member briefs…" />;
  if (error && !usage) return <ErrorNote error={error} />;
  if (!usage) return null;
  const angles = client === 'all' ? usage.angles : usage.angles.filter((a) => a.client_uid === client);
  const handoffs = client === 'all' ? usage.handoffs : usage.handoffs.filter((h) => h.client_uid === client);
  const citedItems = Object.keys(usage.items).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <select className="text-sm border border-slate-300 rounded-lg px-2 py-1" value={client} onChange={(e) => setClient(e.target.value)} aria-label="Member">
          <option value="all">All members ({usage.members.length})</option>
          {usage.members.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <span className="text-xs text-slate-500">
          {usage.angles.length} angles · {usage.handoffs.length} handoffs · {citedItems} items used
        </span>
        <button type="button" onClick={onReload} disabled={loading} className={clsx('ml-auto text-xs underline text-slate-600', loading && 'opacity-50')}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {usage.members.length === 0 && <Empty>No client draws on this library, so nothing can cite it.</Empty>}
      <UnavailableMembers unavailable={usage.unavailable} />
      <Section title={`Angles citing library items (${angles.length})`}>
        <AnglesTable angles={angles} onOpenItem={onOpenItem} />
      </Section>
      <Section title={`Handoffs (${handoffs.length})`}>
        <HandoffsTable handoffs={handoffs} onOpenItem={onOpenItem} />
      </Section>
    </div>
  );
}
